import React from 'react';
import { FormControlLabel, Checkbox, Typography } from '@mui/material';
import { SeriesVisibility } from '../types';
import { ANALYSIS_COLORS } from '../chart/constants';

// Series configuration for rendering toggle controls
const SERIES_CONFIG = [
    { key: 'bidLine' as const, label: 'Bid', colorKey: 'bid' as const },
    { key: 'askLine' as const, label: 'Ask', colorKey: 'ask' as const },
    { key: 'fairValueLine' as const, label: 'Target', colorKey: 'fairValue' as const },
    { key: 'uncertaintyBand' as const, label: 'Uncertainty', colorKey: 'uncertaintyBand' as const },
    { key: 'trades' as const, label: 'Trades', colorKey: 'trades' as const },
    { key: 'candles' as const, label: 'Candles', colorKey: 'candles' as const },
] as const;

interface SeriesToggleControlsProps {
    visibility: SeriesVisibility;
    onChange: (visibility: SeriesVisibility) => void;
    disabled?: boolean;
    // Per-series data availability (true = data exists, false = no data, greyed out)
    dataAvailability?: Partial<Record<keyof SeriesVisibility, boolean>>;
}

export const SeriesToggleControls: React.FC<SeriesToggleControlsProps> = ({
    visibility,
    onChange,
    disabled = false,
    dataAvailability,
}) => {
    const handleChange = (key: keyof SeriesVisibility) => {
        onChange({
            ...visibility,
            [key]: !visibility[key],
        });
    };

    // Check if a specific series is disabled (globally disabled OR no data available)
    const isSeriesDisabled = (key: keyof SeriesVisibility): boolean => {
        if (disabled) return true;
        if (dataAvailability && dataAvailability[key] === false) return true;
        return false;
    };

    // Get the color for a series (greyed out if disabled due to no data)
    const getSeriesColor = (key: keyof SeriesVisibility, normalColor: string): string => {
        if (dataAvailability && dataAvailability[key] === false) {
            return 'rgba(0, 0, 0, 0.26)';
        }
        return normalColor;
    };

    return (
        <>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Show:
            </Typography>
            {SERIES_CONFIG.map(({ key, label, colorKey }, index) => (
                <FormControlLabel
                    key={key}
                    control={
                        <Checkbox
                            checked={visibility[key]}
                            onChange={() => handleChange(key)}
                            disabled={isSeriesDisabled(key)}
                            size="small"
                            sx={{
                                color: getSeriesColor(key, ANALYSIS_COLORS[colorKey]),
                                '&.Mui-checked': { color: getSeriesColor(key, ANALYSIS_COLORS[colorKey]) },
                                padding: '4px',
                            }}
                        />
                    }
                    label={
                        <Typography
                            variant="body2"
                            sx={{ color: isSeriesDisabled(key) ? 'text.disabled' : 'inherit' }}
                        >
                            {label}
                        </Typography>
                    }
                    sx={{ marginRight: index < SERIES_CONFIG.length - 1 ? 1 : 0 }}
                />
            ))}
        </>
    );
};
